import { useEffect } from 'react'
import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { getChertoBucketingId } from 'lib/experiments/chertoBucketingId'
import useExpiringVoucher from 'marketing/hooks/useExpiringVoucher'

const IS_EXPERIMENT_OVERRIDDEN_LOCALLY = config[`CHERTO_${ChertoExperiments.expiringVoucherReminder.id}`] !== undefined

/** The member id Cherto was given when it made the decision held in state, if any. */
function selectExpiringVoucherReminderDecisionMemberId(state: App.State): string | undefined {
  return getChertoBucketingId(state.cherto.units, ChertoExperiments.expiringVoucherReminder.id)
}

/**
 * Runs the expiring voucher reminder Cherto experiment (GR-5151): the variant sees the reminder,
 * control never does.
 *
 * Both arms look for an expiring voucher, and a customer is counted only once they have one, the
 * first point the arms differ. Control gets no voucher back, so other voucher prompts behave as if
 * the reminder did not exist.
 *
 * Cherto buckets the experiment by User ID, and a login refreshes the decision. The reminder waits
 * for that post-login sync, so it follows the arm the activation recorded. The decision is only
 * used when Cherto made it for the member who is logged in now: after an account switch without a
 * page load, a failed refresh leaves the previous member's arm in state.
 */
function useExpiringVoucherReminderExperiment() {
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const decisionMemberId = useAppSelector(selectExpiringVoucherReminderDecisionMemberId)
  const isPostLoginExperimentSyncing = useAppSelector(state => state.optimizely.isPostLoginExperimentSyncing)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const { isTurnedOn, variation, triggerActivation } = useExperiment(
    ChertoExperiments.expiringVoucherReminder,
    { canActivate: false },
  )

  // A local override has no unit to compare.
  const isDecisionForCurrentMember = IS_EXPERIMENT_OVERRIDDEN_LOCALLY || (!!memberId && decisionMemberId === memberId)
  // Waits whatever the decision in state says: an anonymous page load has no decision for a
  // Customer BK experiment until the sync brings one.
  const isAwaitingExperimentRebucket = !!memberId && isPostLoginExperimentSyncing
  // A spoofed session is an admin viewing the customer's account, not the customer seeing the reminder.
  const isDecisionSettled = isDecisionForCurrentMember && !isAwaitingExperimentRebucket && !isSpoofed
  const expiringVoucher = useExpiringVoucher({ enabled: isTurnedOn && isDecisionForCurrentMember && !isSpoofed })
  const canActivate = isDecisionSettled && !!expiringVoucher.voucher

  useEffect(() => {
    if (canActivate) triggerActivation()
  }, [canActivate, triggerActivation])

  const isReminderShown = isDecisionSettled && variation === 'variant'

  return {
    ...expiringVoucher,
    voucher: isReminderShown ? expiringVoucher.voucher : undefined,
    // Hold other voucher prompts back until the post-login sync settles the arm.
    isReady: expiringVoucher.isReady && !isAwaitingExperimentRebucket,
  }
}

export default useExpiringVoucherReminderExperiment
